import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { VOLUNTEER } from "@/constants/testIds";
import { CheckCircle2, XCircle, CheckCheck } from "lucide-react";
import { ShowMore } from "./ShowMore";

const INITIAL_PENDING = 6;

/**
 * Item 6 (2026-07): pending hour approvals grouped by opportunity, each group
 * with an "Approve all" button (confirm dialog) that calls the bulk-approve
 * endpoint. Individual approve/reject stays per row.
 */
export function PendingHoursList({ pending, approve, reject, bulkApprove }) {
  const [limit, setLimit] = useState(INITIAL_PENDING);
  const [confirmGroup, setConfirmGroup] = useState(null); // { label, ids }

  const groups = useMemo(() => {
    const map = new Map();
    for (const p of pending) {
      const key = p.opportunity_id || "general";
      if (!map.has(key)) map.set(key, { key, label: p.opportunity_title || "General hours", logs: [] });
      map.get(key).logs.push(p);
    }
    return [...map.values()];
  }, [pending]);

  // Pagination budget across ALL groups so the list never renders hundreds
  // of rows at once (same contract as the old flat list).
  let budget = limit;
  const rendered = [];
  for (const g of groups) {
    if (budget <= 0) break;
    const rows = g.logs.slice(0, budget);
    budget -= rows.length;
    rendered.push({ g, rows });
  }
  const shownCount = rendered.reduce((s, r) => s + r.rows.length, 0);

  return (
    <>
      <div className="mt-4 space-y-5">
        {pending.length === 0 && <div className="text-neutral-500 text-sm">All caught up.</div>}
        {rendered.map(({ g, rows }) => (
          <div key={g.key} data-testid={`pending-group-${g.key}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 font-bold">
                {g.label} · {g.logs.length} {g.logs.length === 1 ? "log" : "logs"}
              </div>
              {g.logs.length > 1 && (
                <Button
                  data-testid={`bulk-approve-btn-${g.key}`}
                  variant="outline"
                  onClick={() => setConfirmGroup({ label: g.label, ids: g.logs.map((l) => l.id) })}
                  className="rounded-full h-8 px-3.5 text-xs border-forest/25 text-forest hover:bg-forest/5"
                >
                  <CheckCheck className="w-3.5 h-3.5 mr-1.5" /> Approve all ({g.logs.length})
                </Button>
              )}
            </div>
            <div className="space-y-3">
              {rows.map((p) => (
                <div key={p.id} className="bg-white border border-neutral-200 rounded-xl p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-medium text-forest">{p.volunteer_name}</div>
                      <div className="text-sm text-neutral-600">{p.opportunity_title || "—"} · {new Date(p.date).toLocaleDateString()}</div>
                    </div>
                    <div className="font-display text-xl text-forest">{p.hours}h</div>
                  </div>
                  <div className="text-sm text-neutral-700 mt-3">{p.narrative}</div>
                  <div className="flex gap-2 mt-4">
                    <Button data-testid={VOLUNTEER.approveBtn(p.id)} onClick={() => approve(p.id)} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-sm">
                      <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                    </Button>
                    <Button data-testid={VOLUNTEER.rejectBtn(p.id)} onClick={() => reject(p.id)} variant="outline" className="rounded-full h-9 px-4 text-sm">
                      <XCircle className="w-4 h-4 mr-1.5" /> Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <ShowMore
        shown={shownCount}
        total={pending.length}
        onMore={() => setLimit((l) => l + INITIAL_PENDING)}
        testId="pending-hours-show-more"
      />

      <AlertDialog open={!!confirmGroup} onOpenChange={(open) => { if (!open) setConfirmGroup(null); }}>
        <AlertDialogContent data-testid="bulk-approve-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve all pending logs?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmGroup && `Approve ${confirmGroup.ids.length} pending log${confirmGroup.ids.length === 1 ? "" : "s"} for "${confirmGroup.label}"? Volunteers will be notified.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-approve-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="bulk-approve-confirm"
              onClick={() => { if (confirmGroup) bulkApprove(confirmGroup.ids); setConfirmGroup(null); }}
              className="bg-forest hover:bg-forest-hover"
            >
              Approve all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
